// Spec 084 — características de batelada declaradas pela organização.
//
// A plataforma não conhece nenhuma característica: nem "variedade", nem "fornecedor", nem valor
// algum. A organização declara o nome, o tipo (lista de valores ou texto livre), se é obrigatória e
// em quais LINHAS ela é marcada na origem. Fora dessas linhas ela só aparece por herança (FR-001a).
import { z } from "zod";
import { UuidSchema } from "./common.js";

export const BatchCharacteristicKindSchema = z.enum(["LIST", "TEXT"]);
export type BatchCharacteristicKind = z.infer<typeof BatchCharacteristicKindSchema>;

export const BATCH_CHARACTERISTIC_MAX_VALUES = 100;

export const BatchCharacteristicValueInputSchema = z
  .object({
    /** Presente = valor existente. Mantém o id, e as bateladas antigas passam a exibir o rótulo novo. */
    id: UuidSchema.optional(),
    label: z.string().trim().min(1, "Informe o rótulo do valor.").max(80),
  })
  .strict();
export type BatchCharacteristicValueInput = z.infer<typeof BatchCharacteristicValueInputSchema>;

/** Normalização usada na unicidade do rótulo e do nome. */
export function normalizeLabel(label: string): string {
  return label.trim().toLowerCase();
}

const BaseSchema = z
  .object({
    name: z.string().trim().min(1, "Informe o nome da característica.").max(60),
    kind: BatchCharacteristicKindSchema,
    required: z.boolean(),
    /** Linhas de marcação. Vazio é aceito: a característica fica sem efeito até receber uma. */
    lineIds: z.array(UuidSchema).max(200),
    values: z.array(BatchCharacteristicValueInputSchema).max(BATCH_CHARACTERISTIC_MAX_VALUES),
  })
  .strict();

function checkValues(
  c: { kind?: BatchCharacteristicKind; values?: BatchCharacteristicValueInput[] },
  ctx: z.RefinementCtx
) {
  const values = c.values ?? [];
  if (c.kind === "LIST" && values.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["values"],
      message: "Uma característica de lista precisa de ao menos um valor.",
    });
  }
  if (c.kind === "TEXT" && values.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["values"],
      message: "Uma característica de texto livre não tem lista de valores.",
    });
  }
  const seen = new Set<string>();
  for (const v of values) {
    const key = normalizeLabel(v.label);
    if (seen.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["values"],
        message: `O valor "${v.label.trim()}" aparece mais de uma vez.`,
      });
    }
    seen.add(key);
  }
}

export const BatchCharacteristicRequestSchema = BaseSchema.superRefine(checkValues);
export type BatchCharacteristicRequest = z.infer<typeof BatchCharacteristicRequestSchema>;

/** `kind` é imutável: trocar lista por texto invalidaria todo valor gravado. */
export const BatchCharacteristicUpdateSchema = BaseSchema.omit({ kind: true })
  .partial()
  .strict()
  .superRefine((c, ctx) => {
    // Sem `kind`, só a unicidade dos rótulos é verificável aqui; lista vazia é recusada no service,
    // que conhece o tipo gravado.
    checkValues({ values: c.values }, ctx);
  });
export type BatchCharacteristicUpdate = z.infer<typeof BatchCharacteristicUpdateSchema>;

export const BatchCharacteristicQuerySchema = z.object({ lineId: UuidSchema.optional() }).strict();
export type BatchCharacteristicQuery = z.infer<typeof BatchCharacteristicQuerySchema>;

export const BatchCharacteristicResponseSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  kind: BatchCharacteristicKindSchema,
  required: z.boolean(),
  lines: z.array(z.object({ id: UuidSchema, name: z.string() })),
  values: z.array(z.object({ id: UuidSchema, label: z.string(), position: z.number().int() })),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BatchCharacteristicResponse = z.infer<typeof BatchCharacteristicResponseSchema>;

export const BatchCharacteristicListResponseSchema = z.array(BatchCharacteristicResponseSchema);
